import { backendOld } from "~/api";
import { makeDuid } from "~/utils/common";
import { lsGet, lsRemove, lsSet } from "~/utils/localStorageManager";

const LOCAL_AGENT_LOGIN_TOKEN_CACHE_KEY = "localAgentLoginToken";
const LOGIN_TOKEN_REUSE_MS = 8 * 60 * 60 * 1000;

export type LocalAgentInstallHost = "local" | "stag" | `preview-${number}`;
type LocalAgentInstallEnvironment = {
  isLocal: boolean;
  isPreview: boolean;
  isProd: boolean;
  isStag: boolean;
  previewPrNumber: string | null;
};

type CachedLoginToken = {
  userDuid: string;
  token: string;
  createdAt: string;
  persisted: boolean;
};

const pendingPersistence = new Map<string, Promise<void>>();

const readCachedLoginToken = (userDuid: string): CachedLoginToken | null => {
  const cached = lsGet(LOCAL_AGENT_LOGIN_TOKEN_CACHE_KEY);
  if (!cached) {
    return null;
  }

  try {
    const parsed = JSON.parse(cached) as Partial<CachedLoginToken>;
    const createdAtMs = typeof parsed.createdAt === "string" ? Date.parse(parsed.createdAt) : NaN;
    if (
      parsed.userDuid !== userDuid ||
      typeof parsed.token !== "string" ||
      parsed.token.length === 0 ||
      typeof parsed.createdAt !== "string" ||
      typeof parsed.persisted !== "boolean" ||
      Number.isNaN(createdAtMs) ||
      Date.now() - createdAtMs > LOGIN_TOKEN_REUSE_MS
    ) {
      return null;
    }
    return {
      userDuid: parsed.userDuid,
      token: parsed.token,
      createdAt: parsed.createdAt,
      persisted: parsed.persisted,
    };
  } catch {
    return null;
  }
};

const cacheLoginToken = (token: CachedLoginToken) => {
  lsSet(LOCAL_AGENT_LOGIN_TOKEN_CACHE_KEY, JSON.stringify(token));
};

const markLoginTokenPersisted = (userDuid: string, token: string) => {
  const cachedToken = readCachedLoginToken(userDuid);
  if (cachedToken?.token !== token) {
    return;
  }
  cacheLoginToken({ ...cachedToken, persisted: true });
};

const removeLoginTokenIfCurrent = (userDuid: string, token: string) => {
  const cachedToken = readCachedLoginToken(userDuid);
  if (cachedToken?.token === token) {
    lsRemove(LOCAL_AGENT_LOGIN_TOKEN_CACHE_KEY);
  }
};

const persistLoginToken = (userDuid: string, token: string): Promise<void> => {
  const pending = pendingPersistence.get(token);
  if (pending) {
    return pending;
  }

  const persistence = (async () => {
    try {
      await backendOld.auth.makeLoginToken(token);
      markLoginTokenPersisted(userDuid, token);
    } catch (ex) {
      removeLoginTokenIfCurrent(userDuid, token);
      throw ex;
    } finally {
      pendingPersistence.delete(token);
    }
  })();

  pendingPersistence.set(token, persistence);
  return persistence;
};

const createOptimisticLoginToken = (userDuid: string) => {
  const token = makeDuid();
  lsSet(
    LOCAL_AGENT_LOGIN_TOKEN_CACHE_KEY,
    JSON.stringify({
      userDuid,
      token,
      createdAt: new Date().toISOString(),
      persisted: false,
    } satisfies CachedLoginToken)
  );
  return token;
};

export const getReusableLoginToken = (userDuid: string): { token: string; tokenPersistence: Promise<void> } => {
  let cachedToken = readCachedLoginToken(userDuid);
  if (!cachedToken) {
    const token = createOptimisticLoginToken(userDuid);
    cachedToken = readCachedLoginToken(userDuid);
    if (!cachedToken) {
      throw new Error("Couldn't cache login token.");
    }
    return { token, tokenPersistence: persistLoginToken(userDuid, token) };
  }

  if (cachedToken.persisted) {
    return { token: cachedToken.token, tokenPersistence: Promise.resolve() };
  }

  return { token: cachedToken.token, tokenPersistence: persistLoginToken(userDuid, cachedToken.token) };
};

export const getLocalAgentInstallCommand = (
  userDuid: string,
  agentDuid: string,
  installHost?: LocalAgentInstallHost,
  isWindows?: boolean
): { command: string; tokenPersistence: Promise<void> } => {
  const { token, tokenPersistence } = getReusableLoginToken(userDuid);
  const hostArgs = installHost ? `--host ${installHost} ` : "";

  return {
    command: isWindows
      ? `& ([ScriptBlock]::Create((irm https://dartai.com/install.ps1))) ${hostArgs}${token} ${agentDuid}`
      : `curl -fsSL https://dartai.com/install.sh | sh -s -- ${hostArgs}${token} ${agentDuid}`,
    tokenPersistence,
  };
};

export const getLocalAgentInstallHost = (
  environment: LocalAgentInstallEnvironment
): LocalAgentInstallHost | undefined => {
  if (environment.isProd) {
    return undefined;
  }
  if (environment.isLocal) {
    return "local";
  }
  if (environment.isStag) {
    return "stag";
  }
  if (environment.isPreview && environment.previewPrNumber) {
    return `preview-${environment.previewPrNumber}` as LocalAgentInstallHost;
  }
  return undefined;
};
